import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { AmountInput } from "../components/ui/amount-input";
import { Plus, Trash2, Save } from "lucide-react";
import { formatINR } from "../lib/format";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

export default function GroupMembersPanel({ loanId, loanType, principal }) {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [members, setMembers] = useState([]);
    const [busy, setBusy] = useState(false);

    async function load() {
        const { data } = await api.get(`/loans/${loanId}/members`);
        setMembers(data.members || []);
    }
    useEffect(() => {
        if (loanType === "group") load();
        // eslint-disable-next-line
    }, [loanId, loanType]);

    function setM(i, k, v) {
        setMembers((arr) => arr.map((m, idx) => (idx === i ? { ...m, [k]: v } : m)));
    }
    function addMember() {
        setMembers((arr) => [...arr, { name: "", mobile: "", share_amount: 0, notes: "" }]);
    }
    function delMember(i) {
        setMembers((arr) => arr.filter((_, idx) => idx !== i));
    }

    async function save() {
        setBusy(true);
        try {
            const payload = {
                members: members.map((m) => ({
                    ...m,
                    share_amount: parseFloat(m.share_amount) || 0,
                })),
            };
            const { data } = await api.put(`/loans/${loanId}/members`, payload);
            setMembers(data.members);
            toast.success("Members saved");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    if (loanType !== "group") return null;

    const total = members.reduce((s, m) => s + (parseFloat(m.share_amount) || 0), 0);
    const diff = Math.round((principal - total) * 100) / 100;

    return (
        <Card className="rounded-2xl border-slate-100 p-5 sm:p-6">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <h3 className="font-display text-base font-bold text-slate-900">Group Members</h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                        Member shares must sum to principal {formatINR(principal)}
                    </p>
                </div>
                <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                        Allocated
                    </div>
                    <div className={`num font-bold ${Math.abs(diff) < 1 ? "text-emerald-700" : "text-amber-700"}`}>
                        {formatINR(total)}
                    </div>
                    {Math.abs(diff) >= 1 && (
                        <div className="text-[11px] text-amber-700">
                            {diff > 0 ? `Short by ${formatINR(diff)}` : `Over by ${formatINR(-diff)}`}
                        </div>
                    )}
                </div>
            </div>

            {members.length === 0 ? (
                <div className="p-6 text-center text-sm text-slate-400 border border-dashed border-slate-200 rounded-xl">
                    No members yet
                </div>
            ) : (
                <div className="space-y-2.5">
                    {members.map((m, i) => (
                        <Card key={m.id || i} className="rounded-xl border-slate-100 p-3" data-testid={`member-row-${i}`}>
                            <div className="grid sm:grid-cols-12 gap-2 items-start">
                                <div className="sm:col-span-3">
                                    <Label className="text-[10px] uppercase tracking-wider text-slate-500">Name</Label>
                                    <Input
                                        data-testid={`member-name-${i}`}
                                        value={m.name}
                                        disabled={!isAdmin}
                                        onChange={(e) => setM(i, "name", e.target.value)}
                                        className="h-9 rounded-lg"
                                    />
                                </div>
                                <div className="sm:col-span-2">
                                    <Label className="text-[10px] uppercase tracking-wider text-slate-500">Mobile</Label>
                                    <Input
                                        value={m.mobile || ""}
                                        disabled={!isAdmin}
                                        onChange={(e) => setM(i, "mobile", e.target.value)}
                                        className="h-9 rounded-lg"
                                    />
                                </div>
                                <div className="sm:col-span-2">
                                    <Label className="text-[10px] uppercase tracking-wider text-slate-500">Share ₹</Label>
                                    <AmountInput
                                        data-testid={`member-share-${i}`}
                                        value={m.share_amount || ""}
                                        disabled={!isAdmin}
                                        onChange={(v) => setM(i, "share_amount", v)}
                                        className="h-9 rounded-lg"
                                    />
                                </div>
                                <div className="sm:col-span-2">
                                    <Label className="text-[10px] uppercase tracking-wider text-slate-500">Paid</Label>
                                    <div className="h-9 grid items-center text-sm font-semibold text-emerald-700 num">
                                        {formatINR(m.principal_paid || 0)}
                                    </div>
                                </div>
                                <div className="sm:col-span-2">
                                    <Label className="text-[10px] uppercase tracking-wider text-slate-500">Pending</Label>
                                    <div className="h-9 grid items-center text-sm font-semibold text-slate-900 num">
                                        {formatINR(m.pending_share ?? m.share_amount)}
                                    </div>
                                </div>
                                <div className="sm:col-span-1 flex items-end justify-end h-full">
                                    {isAdmin && (
                                        <Button
                                            size="icon"
                                            variant="ghost"
                                            onClick={() => delMember(i)}
                                            data-testid={`delete-member-${m.id || i}`}
                                            className="text-rose-600 hover:bg-rose-50"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    )}
                                </div>
                            </div>
                            {(m.total_paid ?? 0) > 0 && (
                                <div className="mt-2 flex items-center gap-2 text-[11px] flex-wrap">
                                    <Badge className="bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-50">
                                        Total paid {formatINR(m.total_paid)}
                                    </Badge>
                                    <span className="text-slate-500">
                                        (interest {formatINR(m.interest_paid)})
                                    </span>
                                </div>
                            )}
                            {Array.isArray(m.recent_deductions) && m.recent_deductions.length > 0 && (
                                <div className="mt-2 pt-2 border-t border-slate-100">
                                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
                                        Recent deductions
                                    </div>
                                    <ul className="space-y-0.5" data-testid={`member-deductions-${i}`}>
                                        {m.recent_deductions.map((d, di) => (
                                            <li key={d.id || `${d.payment_date || ""}-${di}`} className="text-[11px] text-slate-600 flex items-center justify-between gap-2">
                                                <span className="truncate">
                                                    {(d.payment_date || "").slice(0, 10)} · {d.source === "group_split" ? "Group split" : "Direct"}
                                                </span>
                                                <span className="num font-semibold text-slate-900">
                                                    -{formatINR(d.total)}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </Card>
                    ))}
                </div>
            )}

            {isAdmin && (
                <div className="mt-3 flex gap-2">
                    <Button variant="outline" onClick={addMember} className="h-10 rounded-xl" data-testid="add-member-btn">
                        <Plus className="h-4 w-4 mr-1.5" /> Add member
                    </Button>
                    <Button
                        onClick={save}
                        disabled={busy || Math.abs(diff) >= 1}
                        data-testid="save-members-btn"
                        className="h-10 rounded-xl bg-blue-900 hover:bg-blue-800"
                    >
                        <Save className="h-4 w-4 mr-1.5" /> Save members
                    </Button>
                </div>
            )}
        </Card>
    );
}
